function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { identify, setBaseProperties, waitForBaseProperties } from '@adsk/acs-web-analytics';
import { AlloyProvider, useUser } from '@adsk/alloy-provider';
import TopNavigation from '@adsk/alloy-top-navigation';
import MaintenancePageConditionalRenderer from './MaintenancePageConditionalRenderer';
import { initialize as initializePendo } from './pendo';
import TopNavigationLayout from './TopNavigationLayout';
import { useProductsAccessTypes } from './useProductsAccessTypes';
const ConnectedTopNavigation = props => {
  const {
    user
  } = useUser();
  const {
    UNSAFE_showBuildersUserProfileModal,
    UNSAFE_isBuildersProfileModalEnabled
  } = props;
  useEffect(() => {
    if (user) {
      initializePendo({
        user
      });
    }
  }, [user]);
  return /*#__PURE__*/React.createElement(TopNavigation, {
    logo: true,
    user: user,
    showHelpMenu: true,
    showTrialBanner: true,
    showSubscriptionsModal: true,
    helpLinks: {
      help: 'https://help.autodesk.com/view/BUILD/ENU/?guid=Project_Administration'
    },
    isProductRequired: false,
    UNSAFE_showBuildersUserProfileModal: UNSAFE_showBuildersUserProfileModal,
    UNSAFE_isBuildersProfileModalEnabled: UNSAFE_isBuildersProfileModalEnabled
  });
};
const AnalyticsInitializer = () => {
  const {
    user
  } = useUser();
  const notApplicable = 'N/A';
  useLayoutEffect(() => {
    waitForBaseProperties('license');
  }, []);
  const {
    data: productsAccessTypesData,
    loading: productsAccessTypesLoading
  } = useProductsAccessTypes();
  useEffect(() => {
    !productsAccessTypesLoading && user && identify(user.analyticsId, _objectSpread(_objectSpread({}, user), {}, {
      licenses: productsAccessTypesData
    }));
  }, [productsAccessTypesData, productsAccessTypesLoading, user]);
  useEffect(() => {
    setBaseProperties({
      license: notApplicable,
      tool_id: notApplicable,
      product_id: notApplicable,
      account_id: notApplicable,
      project_id: notApplicable
    });
  }, []);
  return null;
};

/**
 * ProjectDirectoryLayout component is the root component for project list page views.
 * This component needs to be as up as we can in React components tree, and handles 3 things mainly:
 *
 * 1. Page layout - rendering top navigation component, including projects picker, product picker and more, all according to user's entitlements.
 * 2. Data providers - adds AlloyProvider, which fetches relevant data and allows all children to access the data using hooks (useUser, useProjectEntitlements, etc.)
 * 3. Redirects - redirect the user to pages they can see:
 *       * User is not logged in - redirects to login page.
 *       * When entitlements response contains `valid_service_location` the user needs to go to.
 */
const ProjectDirectoryLayout = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    children,
    locale,
    renderAboveLayout,
    autoFetchEntities = [],
    UNSAFE_showBuildersUserProfileModal,
    UNSAFE_isBuildersProfileModalEnabled
  } = _ref;
  return /*#__PURE__*/React.createElement(AlloyProvider, {
    locale: locale,
    autoFetchEntities: autoFetchEntities
  }, /*#__PURE__*/React.createElement(AnalyticsInitializer, null), /*#__PURE__*/React.createElement(MaintenancePageConditionalRenderer, null, /*#__PURE__*/React.createElement(TopNavigationLayout, {
    ref: ref,
    renderTopNavigation: props => /*#__PURE__*/React.createElement(ConnectedTopNavigation, _extends({}, props, {
      UNSAFE_showBuildersUserProfileModal: UNSAFE_showBuildersUserProfileModal,
      UNSAFE_isBuildersProfileModalEnabled: UNSAFE_isBuildersProfileModalEnabled
    })),
    renderAboveLayout: renderAboveLayout
  }, children)));
});
ProjectDirectoryLayout.displayName = 'ProjectDirectoryLayout';
ProjectDirectoryLayout.AUTO_FETCH_TYPES = AlloyProvider.AUTO_FETCH_TYPES;
ProjectDirectoryLayout.propTypes = {
  /** Page content */
  children: PropTypes.node,
  /** localization, default: en */
  locale: PropTypes.string,
  /** Items that will render above the layout*/
  renderAboveLayout: PropTypes.func,
  /** Auto fetch for specific types. Can be used to remove fetching some data if not needed (users, companies, etc.) */
  autoFetchEntities: PropTypes.arrayOf(PropTypes.oneOf(Object.values(AlloyProvider.AUTO_FETCH_TYPES))),
  /** Triggers opening the Builders Network Profile Modal */
  UNSAFE_showBuildersUserProfileModal: PropTypes.func,
  /** When BuildersNetworkProfile is enabled, it updates the TopNav Profile dropdown options to include button for opening the BN Modal and hides the Profile settings button*/
  UNSAFE_isBuildersProfileModalEnabled: PropTypes.bool
};
ConnectedTopNavigation.propTypes = {
  /** Triggers opening the Builders Network Profile Modal */
  UNSAFE_showBuildersUserProfileModal: PropTypes.func,
  /** When BuildersNetworkProfile is enabled, it updates the TopNav Profile dropdown options to include button for opening the BN Modal and hides the Profile settings button*/
  UNSAFE_isBuildersProfileModalEnabled: PropTypes.bool
};
export default ProjectDirectoryLayout;
//# sourceMappingURL=ProjectDirectoryLayout.js.map